import * as React from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';

import { cn } from '@/ux-common/lib/utils';

/**
 * Switch component - Minimal React wrapper for behavioral concerns.
 *
 * All visual styling is applied via CSS classes. This wrapper provides:
 * - Radix primitive for accessible state management (role="switch", aria-checked, keyboard)
 * - Ref forwarding to the underlying button element
 * - Optional decorative thumb content via the `thumbIcon` slot, rendered
 *   `aria-hidden` inside the thumb
 * - `data-component="Switch"` for component identification
 * - `className` merging via `cn()`
 *
 * The `.switch` base class is applied automatically. Compose the `switch-inverse`
 * variant (light surface) via `className`.
 *
 * @example
 * ```tsx
 * // Standalone switch (gets .switch — default green ON)
 * <Switch checked={value} onCheckedChange={setValue} />
 *
 * // With a decorative thumb icon
 * <Switch thumbIcon={<Check />} checked={on} onCheckedChange={setOn} />
 *
 * // Inverse variant (light surface)
 * <Switch className="switch-inverse" checked={on} onCheckedChange={setOn} />
 * ```
 */

export type SwitchProps = React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> & {
  /** Optional content rendered inside the thumb (e.g. a small icon). */
  thumbIcon?: React.ReactNode;
};

export const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ className, thumbIcon, ...props }, ref) => (
    <SwitchPrimitives.Root
      ref={ref}
      data-component="Switch"
      className={cn('switch', className)}
      {...props}
    >
      <SwitchPrimitives.Thumb className="switch-thumb">
        {thumbIcon ? (
          <span aria-hidden="true" className="switch-icon">
            {thumbIcon}
          </span>
        ) : null}
      </SwitchPrimitives.Thumb>
    </SwitchPrimitives.Root>
  )
);

Switch.displayName = 'Switch';
